import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Archive, Loader2, Save, Trash2 } from "lucide-react";

type Settings = {
  audit_retention_days: number;
  failures_retention_days: number;
  resolved_failures_retention_days: number;
  last_run_at: string | null;
  last_run_audit_deleted: number;
  last_run_failures_deleted: number;
};

const clampInt = (v: unknown, min = 1, max = 3650) =>
  Math.max(min, Math.min(max, Number(v) || min));

export function HelpdeskAlertRetentionPanel() {
  const [s, setS] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<null | "save" | "purge">(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("helpdesk_alert_retention_settings" as any)
      .select(
        "audit_retention_days,failures_retention_days,resolved_failures_retention_days,last_run_at,last_run_audit_deleted,last_run_failures_deleted",
      )
      .maybeSingle();
    setLoading(false);
    if (error) return toast.error(error.message);
    setS(data as unknown as Settings);
  };
  useEffect(() => { void load(); }, []);

  const save = async () => {
    if (!s) return;
    setBusy("save");
    const { error } = await supabase
      .from("helpdesk_alert_retention_settings" as any)
      .update({
        audit_retention_days: s.audit_retention_days,
        failures_retention_days: s.failures_retention_days,
        resolved_failures_retention_days: s.resolved_failures_retention_days,
      })
      .eq("id", true);
    setBusy(null);
    if (error) return toast.error(error.message);
    toast.success("Retention settings saved");
  };

  const purge = async () => {
    setBusy("purge");
    const { data, error } = await supabase.rpc("run_helpdesk_alert_purge" as any);
    setBusy(null);
    if (error) return toast.error(error.message);
    const r = (data ?? {}) as { audit_deleted?: number; failures_deleted?: number };
    toast.success("Purge complete", {
      description: `Audit rows deleted ${r.audit_deleted ?? 0} · Failure rows deleted ${r.failures_deleted ?? 0}`,
    });
    void load();
  };

  if (loading || !s) {
    return <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">Loading alert retention settings…</div>;
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Archive className="size-4" /> Alert route audit & delivery failure retention
          </h3>
          <p className="text-sm text-muted-foreground">
            Controls database growth for <code>helpdesk_alert_route_audit</code> and{" "}
            <code>helpdesk_alert_delivery_failures</code>. Purge runs automatically every day at{" "}
            <span className="font-mono">03:15 UTC</span>; you can also run it on demand below.
          </p>
        </div>
      </div>

      {s.last_run_at && (
        <div className="rounded-lg border border-border/60 bg-muted/30 p-3 text-xs">
          <div className="font-medium mb-1.5">Last purge · {new Date(s.last_run_at).toLocaleString()}</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 max-w-md">
            <div className="flex items-center justify-between rounded bg-background/60 px-2 py-1.5">
              <span className="text-muted-foreground">helpdesk_alert_route_audit</span>
              <span className="font-mono">{s.last_run_audit_deleted} deleted</span>
            </div>
            <div className="flex items-center justify-between rounded bg-background/60 px-2 py-1.5">
              <span className="text-muted-foreground">helpdesk_alert_delivery_failures</span>
              <span className="font-mono">{s.last_run_failures_deleted} deleted</span>
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label>Audit retention (days)</Label>
          <Input type="number" min={1} max={3650} value={s.audit_retention_days}
            onChange={(e) => setS({ ...s, audit_retention_days: clampInt(e.target.value) })} />
        </div>
        <div className="space-y-1.5">
          <Label>Failure retention (days)</Label>
          <Input type="number" min={1} max={3650} value={s.failures_retention_days}
            onChange={(e) => setS({ ...s, failures_retention_days: clampInt(e.target.value) })} />
        </div>
        <div className="space-y-1.5">
          <Label>Resolved failure retention (days)</Label>
          <Input type="number" min={1} max={3650} value={s.resolved_failures_retention_days}
            onChange={(e) => setS({ ...s, resolved_failures_retention_days: clampInt(e.target.value) })} />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={purge} disabled={!!busy}>
          {busy === "purge" ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
          Run purge now
        </Button>
        <Button onClick={save} disabled={!!busy}>
          {busy === "save" ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save settings
        </Button>
      </div>
    </div>
  );
}


export default HelpdeskAlertRetentionPanel;
